import React from "react";
import styled from "styled-components";
import { Stack, Tooltip } from "@mui/material";
import ShowWithSkeleton from "src/utils/show-with-skeleton";
import CustomTooltip from "src/components/custom-tooltip";
import GroupIcon from "@mui/icons-material/Group";

const SubIndexValue = styled.p`
    font-size: 40px;
    font-weight: 500;
    line-height: 1;
    margin-top: 0.8rem;
    margin-bottom: 15px;
`;

const SubIndexHelper = styled.span`
    font-size: 12px;
    font-weight: 200;
    opacity: 0.5;
`;

const CardTitle = styled.h2`
    font-size: 14px;
    font-weight: 400;
`;

const TitleStack = styled(Stack)`
    min-width: 100px;
    justify-content: center;
`;

const BenchmarkText = styled.p`
    font-size: 16px;
    font-weight: 500;
    margin: 0;
`;

const ColorIndicator = styled.div`
    height: 10px;
    width: 10px;
    border-radius: 2px;
    margin-left: 6px;
    background-color: ${({ name, theme }) => {
        if (name === "work") {
            return theme.palette.background.work;
        }
        if (name === "collaboration") {
            return theme.palette.background.collaboration;
        }
        if (name === "wellbeing") {
            return theme.palette.background.wellbeing;
        }
        return null;
    }};
`;

const OverviewIndexMetrics = ({ fetchAction, isTeams }) => {
    if (!fetchAction) return null;
    const { data } = fetchAction;
    return (
        <Stack direction="row" justifyContent="space-between" p={2} style={{ width: "100%" }}>
            <Stack direction="column" alignItems="center">
                <TitleStack direction="row" alignItems="center">
                    <CardTitle>Work</CardTitle>
                    <ColorIndicator name="work" />
                </TitleStack>
                <ShowWithSkeleton
                    fetchAction={fetchAction}
                    height="70%"
                    width="100%"
                    streamingProperty={fetchAction.data && fetchAction.data.mean}
                >
                    {data?.mean?.work === null ? (
                        <Tooltip title="No data avaible for this index">
                            <SubIndexValue>N|D</SubIndexValue>
                        </Tooltip>
                    ) : (
                        <SubIndexValue>
                            {data?.mean?.work?.toFixed(1)}
                            <SubIndexHelper> / 10</SubIndexHelper>
                        </SubIndexValue>
                    )}
                </ShowWithSkeleton>
                {!isTeams && (
                    <Stack
                        direction="row"
                        justifyContent="center"
                        alignItems="center"
                        spacing={1}
                        style={{ width: "100%", opacity: "0.5" }}
                    >
                        <CustomTooltip title="Company Work Average">
                            <GroupIcon />
                        </CustomTooltip>
                        <ShowWithSkeleton
                            fetchAction={fetchAction}
                            height="70%"
                            width="100%"
                            streamingProperty={fetchAction.data && fetchAction.data.orgBaseline}
                        >
                            <BenchmarkText>
                                {data?.orgBaseline?.work
                                    ? data.orgBaseline.work?.toFixed(1)
                                    : "N/A"}
                            </BenchmarkText>
                        </ShowWithSkeleton>
                    </Stack>
                )}
            </Stack>

            <Stack direction="column" alignItems="center">
                <TitleStack direction="row" alignItems="center">
                    <CardTitle>Collaboration</CardTitle>
                    <ColorIndicator name="collaboration" />
                </TitleStack>
                <ShowWithSkeleton
                    fetchAction={fetchAction}
                    height="70%"
                    width="100%"
                    streamingProperty={fetchAction.data && fetchAction.data.mean}
                >
                    {data?.mean?.collaboration === null ? (
                        <Tooltip title="No data avaible for this index">
                            <SubIndexValue>N|D</SubIndexValue>
                        </Tooltip>
                    ) : (
                        <SubIndexValue>
                            {data?.mean?.collaboration?.toFixed(1)}
                            <SubIndexHelper> / 10</SubIndexHelper>
                        </SubIndexValue>
                    )}
                </ShowWithSkeleton>
                {!isTeams && (
                    <Stack
                        direction="row"
                        justifyContent="center"
                        alignItems="center"
                        spacing={1}
                        style={{ width: "100%", opacity: "0.5" }}
                    >
                        <CustomTooltip title="Company Collaboration Average">
                            <GroupIcon />
                        </CustomTooltip>
                        <ShowWithSkeleton
                            fetchAction={fetchAction}
                            height="70%"
                            width="100%"
                            streamingProperty={fetchAction.data && fetchAction.data.orgBaseline}
                        >
                            <BenchmarkText>
                                {data?.orgBaseline?.collaboration ? (
                                    data.orgBaseline.collaboration?.toFixed(1)
                                ) : (
                                    <Tooltip title="No data avaible for this index">
                                        <BenchmarkText>N|D</BenchmarkText>
                                    </Tooltip>
                                )}
                            </BenchmarkText>
                        </ShowWithSkeleton>
                    </Stack>
                )}
            </Stack>

            <Stack direction="column" alignItems="center">
                <TitleStack direction="row" alignItems="center">
                    <CardTitle>Wellbeing</CardTitle>
                    <ColorIndicator name="wellbeing" />
                </TitleStack>
                <ShowWithSkeleton
                    fetchAction={fetchAction}
                    height="70%"
                    width="100%"
                    streamingProperty={fetchAction.data && fetchAction.data.mean}
                >
                    {data?.mean?.wellbeing === null ? (
                        <Tooltip title="No data avaible for this index">
                            <SubIndexValue>N|D</SubIndexValue>
                        </Tooltip>
                    ) : (
                        <SubIndexValue>
                            {data?.mean?.wellbeing?.toFixed(1)}
                            <SubIndexHelper> / 10</SubIndexHelper>
                        </SubIndexValue>
                    )}
                </ShowWithSkeleton>
                {!isTeams && (
                    <Stack
                        direction="row"
                        justifyContent="center"
                        alignItems="center"
                        spacing={1}
                        style={{ width: "100%", opacity: "0.5" }}
                    >
                        <CustomTooltip title="Company Wellbeing Average">
                            <GroupIcon />
                        </CustomTooltip>
                        <ShowWithSkeleton
                            fetchAction={fetchAction}
                            height="70%"
                            width="100%"
                            streamingProperty={fetchAction.data && fetchAction.data.orgBaseline}
                        >
                            <BenchmarkText>
                                {data?.orgBaseline?.wellbeing ? (
                                    data.orgBaseline.wellbeing?.toFixed(1)
                                ) : (
                                    <Tooltip title="No data avaible for this index">
                                        <BenchmarkText>N|D</BenchmarkText>
                                    </Tooltip>
                                )}
                            </BenchmarkText>
                        </ShowWithSkeleton>
                    </Stack>
                )}
            </Stack>
        </Stack>
    );
};

export default OverviewIndexMetrics;
